import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { languages } from "@/i18n";
import { LogOut, User, Wallet as WalletIcon, Shield, Bot, Menu, Search } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const Header = () => {
  const { user, signOut } = useAuth();
  const { isAdmin } = useUserRole();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const currentLang = languages.find(l => l.code === i18n.language);
  const isRtl = currentLang?.dir === 'rtl';
  const iconMargin = isRtl ? 'ml-2' : 'mr-2';

  useEffect(() => {
    document.documentElement.dir = currentLang?.dir || 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language, currentLang]);

  const handleSearch = () => {
    navigate("/dashboard");
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center">
            <WalletIcon className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="text-xl font-bold text-foreground">{t('brand')}</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-4">
          <LanguageSwitcher />
          {user ? (
            <>
              {isAdmin && (
                <Link to="/admin">
                  <Button variant="ghost" className="text-primary hover:bg-primary/10">
                    <Shield className={`w-4 h-4 ${iconMargin}`} />
                    {t('nav.admin')}
                  </Button>
                </Link>
              )}
              <Link to="/wallet">
                <Button variant="ghost" className="text-foreground hover:bg-secondary">
                  <WalletIcon className={`w-4 h-4 ${iconMargin}`} />
                  {t('nav.wallet')}
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="ghost" className="text-foreground hover:bg-secondary">
                  {t('nav.dashboard')}
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={handleSearch} className="text-foreground hover:bg-secondary" aria-label={t('nav.search')}>
                <Search className="w-5 h-5" />
              </Button>
              <Link to="/transactions">
                <Button variant="ghost" className="text-foreground hover:bg-secondary">
                  {t('nav.transactions')}
                </Button>
              </Link>
              <Link to="/chat">
                <Button variant="ghost" className="text-foreground hover:bg-secondary">
                  <Bot className={`w-4 h-4 ${iconMargin}`} />
                  {t('nav.aiChat')}
                </Button>
              </Link>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary">
                <User className="w-4 h-4 text-primary" />
                <span className="text-sm text-muted-foreground">{user.email}</span>
              </div>
              <Link to="/profile">
                <Button variant="outline" className="gap-2">
                  <User className="w-4 h-4" />
                  {t('nav.profile') || 'الملف الشخصي'}
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={handleSignOut} className="text-muted-foreground hover:text-destructive">
                <LogOut className="w-5 h-5" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/auth">
                <Button variant="ghost" className="text-foreground hover:bg-secondary">
                  {t('auth.signIn')}
                </Button>
              </Link>
              <Link to="/auth?mode=signup">
                <Button className="gradient-primary text-primary-foreground">{t('auth.signUp')}</Button>
              </Link>
            </>
          )}
        </nav>

        {/* Mobile nav */}
        <div className="lg:hidden flex items-center gap-1">
          <LanguageSwitcher />
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={t('nav.menu')}>
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side={isRtl ? "right" : "left"} className="w-[320px] sm:w-[380px]">
              <div className="flex flex-col gap-3">
                <div>
                  <div className="text-base font-semibold text-foreground">{t('nav.menu')}</div>
                  <div className="text-sm text-muted-foreground">{t('nav.menuSubtitle')}</div>
                </div>
                <div className="h-px bg-border" />
                {user ? (
                  <>
                    {isAdmin && (
                      <Link to="/admin" onClick={closeMobileMenu} className="w-full">
                        <Button variant="ghost" className="w-full justify-start">
                          <Shield className={`w-4 h-4 ${iconMargin}`} />
                          {t('nav.admin')}
                        </Button>
                      </Link>
                    )}
                    <Link to="/wallet" onClick={closeMobileMenu} className="w-full">
                      <Button variant="ghost" className="w-full justify-start">
                        <WalletIcon className={`w-4 h-4 ${iconMargin}`} />
                        {t('nav.wallet')}
                      </Button>
                    </Link>
                    <Link to="/dashboard" onClick={closeMobileMenu} className="w-full">
                      <Button variant="ghost" className="w-full justify-start">{t('nav.dashboard')}</Button>
                    </Link>
                    <Button variant="ghost" className="w-full justify-start" onClick={() => { closeMobileMenu(); handleSearch(); }}>
                      <Search className={`w-4 h-4 ${iconMargin}`} />
                      {t('nav.searchCrypto')}
                    </Button>
                    <Link to="/transactions" onClick={closeMobileMenu} className="w-full">
                      <Button variant="ghost" className="w-full justify-start">{t('nav.transactions')}</Button>
                    </Link>
                    <Link to="/chat" onClick={closeMobileMenu} className="w-full">
                      <Button variant="ghost" className="w-full justify-start">
                        <Bot className={`w-4 h-4 ${iconMargin}`} />
                        {t('nav.aiChat')}
                      </Button>
                    </Link>
                    <div className="h-px bg-border" />
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary">
                      <User className="w-4 h-4 text-primary" />
                      <span className="text-sm text-muted-foreground truncate">{user.email}</span>
                    </div>
                    <Link to="/profile" onClick={closeMobileMenu} className="w-full">
                      <Button variant="outline" className="w-full justify-start gap-2">
                        <User className="w-4 h-4" />
                        {t('nav.profile') || 'الملف الشخصي'}
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      onClick={async () => { closeMobileMenu(); await handleSignOut(); }}
                      className="w-full justify-start text-destructive border-destructive/50 hover:bg-destructive/10"
                    >
                      <LogOut className={`w-4 h-4 ${iconMargin}`} />
                      {t('auth.signOut')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/auth" onClick={closeMobileMenu} className="w-full">
                      <Button variant="ghost" className="w-full justify-start">{t('auth.signIn')}</Button>
                    </Link>
                    <Link to="/auth?mode=signup" onClick={closeMobileMenu} className="w-full">
                      <Button className="w-full gradient-primary text-primary-foreground justify-start">{t('auth.signUp')}</Button>
                    </Link>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
